import { useHeavent } from 'common-front/src/hooks/useHeavent';
import { Box } from 'common/src/designSystem/components/box';
import { Flex } from 'common/src/designSystem/components/flex';
import {
    AccreditationsFormsCustomFieldInput,
    FormCustomFieldFragment
} from 'common/src/generated/types';
import { isNonEmptyArray, reorder } from 'common/src/util/array';
import { uuidv4 } from 'common/src/util/uuid';
import { produce } from 'immer';
import * as React from 'react';
import { AttachFormCustomFieldsAddButton } from './attachFormCustomFieldsAddButton';
import { AttachFormCustomFieldsEmpty } from './attachFormCustomFieldsEmpty';
import { AttachFormCustomFieldsList } from './attachFormCustomFieldsList';

interface IAttachFormCustomFieldsProps {
    formCustomFields: FormCustomFieldFragment[];
    formCustomFieldsValues: AccreditationsFormsCustomFieldInput[];
    prefix: string;

    change(name: string, value: any): void;
}

export const AttachFormCustomFields = (props: IAttachFormCustomFieldsProps) => {
    const { translate } = useHeavent();
    const selectedCustomFieldsIds = React.useMemo(
        () => new Set(props.formCustomFieldsValues.map(({ customFieldId }) => customFieldId)),
        [props.formCustomFieldsValues]
    );
    const updateValues = React.useCallback(
        (newValues: AccreditationsFormsCustomFieldInput[]) => {
            props.change(`${props.prefix}formCustomFields`, newValues);
        },
        [props.prefix, props.change]
    );
    const fieldToggle = React.useCallback(
        (field: FormCustomFieldFragment) => {
            if (selectedCustomFieldsIds.has(field.id)) {
                updateValues(
                    props.formCustomFieldsValues.filter(
                        ({ customFieldId }) => customFieldId !== field.id
                    )
                );
            } else {
                updateValues([
                    ...props.formCustomFieldsValues,
                    {
                        id: uuidv4(),
                        customFieldId: field.id,
                        isMandatory: true
                    }
                ]);
            }
        },
        [props.formCustomFieldsValues, selectedCustomFieldsIds, updateValues]
    );
    const onDragEnd = React.useCallback(
        ({ reason, source, destination }) => {
            if (reason === 'DROP' && destination && source.index !== destination.index) {
                updateValues(
                    reorder(props.formCustomFieldsValues, source.index, destination.index)
                );
            }
        },
        [props.formCustomFieldsValues, updateValues]
    );
    const toggleMandatory = React.useCallback(
        (index: number, isMandatory: boolean) => {
            updateValues(
                produce(
                    props.formCustomFieldsValues,
                    (draft: AccreditationsFormsCustomFieldInput[]) => {
                        draft[index].isMandatory = isMandatory;
                    }
                )
            );
        },
        [props.formCustomFieldsValues, updateValues]
    );
    const onDelete = React.useCallback(
        (index: number) => {
            updateValues(
                produce(
                    props.formCustomFieldsValues,
                    (draft: AccreditationsFormsCustomFieldInput[]) => {
                        draft.splice(index, 1);
                    }
                )
            );
        },
        [props.formCustomFieldsValues, updateValues]
    );

    return (
        <Flex gap="7">
            <Flex width={280}>
                <Box font="gray800 textSm medium">{translate('champs_personna_17489')}</Box>
            </Flex>

            <Flex css={{ flex: '1' }}>
                {isNonEmptyArray(props.formCustomFieldsValues) ? (
                    <Flex direction="column" gap="1" width={1}>
                        <AttachFormCustomFieldsList
                            formCustomFields={props.formCustomFields}
                            formCustomFieldsValues={props.formCustomFieldsValues}
                            toggleMandatory={toggleMandatory}
                            onDelete={onDelete}
                            onDragEnd={onDragEnd}
                        />

                        <AttachFormCustomFieldsAddButton
                            formCustomFields={props.formCustomFields}
                            selectedCustomFieldsIds={selectedCustomFieldsIds}
                            toggle={fieldToggle}
                        />
                    </Flex>
                ) : (
                    <AttachFormCustomFieldsEmpty
                        formCustomFields={props.formCustomFields}
                        selectedCustomFieldsIds={selectedCustomFieldsIds}
                        toggle={fieldToggle}
                    />
                )}
            </Flex>
        </Flex>
    );
};
